import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Divider,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";
import EmployeeForm from "../components/EmployeeForm";
import ContractForm, { ComponentItem } from "../components/ContractForm";

const AddEmployeePage: React.FC = () => {
  useProtectedRoute();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [employeeForm, setEmployeeForm] = useState<any>(null); // to store employee form data
  const [contractReady, setContractReady] = useState(false); // control step

  const handleEmployeeSubmit = async (form: any) => {
    setEmployeeForm(form);
    setContractReady(true); // move to contract step
  };

  const handleContractSubmit = async (contractForm: {
    startDate: string;
    endDate?: string;
    salary: number;
    components: ComponentItem[];
  }) => {
    setSaving(true);
    try {
      // Step 1: Create Employee
      const employeeRes = await api.post("/employees", employeeForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const employeeId = employeeRes.data._id;

      // Step 2: Create Contract
      await api.post(
        "/contracts",
        {
          employeeId,
          ...contractForm,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      alert("Employee and contract created successfully.");
      navigate("/employees");
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to create employee/contract.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={4}>
      <Typography variant="h4" fontWeight="bold" mb={2}>
        Add New Employee
      </Typography>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" mb={2}>
          Step 1: Employee Details
        </Typography>
        <EmployeeForm onSubmit={handleEmployeeSubmit} saving={saving} />
      </Paper>

      {contractReady && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" mb={2}>
            Step 2: Contract Details
          </Typography>
          <ContractForm onSubmit={handleContractSubmit} saving={saving} />
        </Paper>
      )}

      {!contractReady && (
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            Fill and submit the employee form first to proceed to contract setup.
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 4 }} />

      <Grid container justifyContent="flex-end">
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => navigate("/employees")}
        >
          Cancel
        </Button>
      </Grid>
    </Box>
  );
};

export default AddEmployeePage;
